import React, { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Card, Button, Alert, Spinner, Row, Col, Badge, Table } from 'react-bootstrap'
import { useAdminIssue } from 'api/hooks/use-admin-issues'
import AdminTabs from 'admin/shared/AdminTabs'

const SEVERITY_LABELS = { 1: 'Low', 2: 'Medium', 3: 'High', 4: 'Critical', 5: 'Severe' }
const SEVERITY_COLORS = { 1: 'secondary', 2: 'info', 3: 'warning', 4: 'danger', 5: 'dark' }

const IssueDetail = () => {
  const { id } = useParams()
  const { data: issue, isLoading, error } = useAdminIssue({ id })
  const [activeTab, setActiveTab] = useState('details')

  if (isLoading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    )
  }

  if (error) {
    return <Alert variant="danger">Error loading issue: {error.message}</Alert>
  }

  if (!issue) {
    return <Alert variant="warning">Issue not found.</Alert>
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Issue #{issue.number} - {issue.name}</h2>
        <div>
          <Button as={Link} to={`/admin/issues/${id}/edit`} variant="warning" className="me-2">Edit Issue</Button>
          <Button as={Link} to="/admin/issues" variant="light-primary">Back to List</Button>
        </div>
      </div>

      <AdminTabs
        tabs={[
          { name: 'Details', value: 'details' },
          { name: `Municipalities (${issue.municipalities?.length || 0})`, value: 'municipalities' }
        ]}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />

      {activeTab === 'details' && (
        <Row>
          <Col md={6}>
            <Card className="mb-4">
              <Card.Header><h5>Basic Information</h5></Card.Header>
              <Card.Body>
                <dl className="row mb-0">
                  <dt className="col-sm-5">Number:</dt>
                  <dd className="col-sm-7">{issue.number}</dd>
                  <dt className="col-sm-5">Name:</dt>
                  <dd className="col-sm-7">{issue.name}</dd>
                  <dt className="col-sm-5">Datatype:</dt>
                  <dd className="col-sm-7">{issue.datatype ? <Badge bg="info">{issue.datatype}</Badge> : 'N/A'}</dd>
                  <dt className="col-sm-5">Severity:</dt>
                  <dd className="col-sm-7">
                    <Badge bg={SEVERITY_COLORS[issue.default_severity] || 'secondary'}>
                      {SEVERITY_LABELS[issue.default_severity] || issue.default_severity}
                    </Badge>
                  </dd>
                </dl>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="mb-4">
              <Card.Header><h5>Descriptions</h5></Card.Header>
              <Card.Body>
                <h6>Description</h6>
                <p>{issue.description || 'N/A'}</p>
                <h6>Resolution</h6>
                <p>{issue.resolution || 'N/A'}</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {activeTab === 'municipalities' && (
        <Card>
          <Card.Body>
            {issue.municipalities?.length > 0 ? (
              <Table bordered hover responsive>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Abbreviation</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {issue.municipalities.map((municipality) => (
                    <tr key={municipality.id}>
                      <td>{municipality.name}</td>
                      <td><code>{municipality.abbreviation}</code></td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <Button as={Link} to={`/admin/municipalities/${municipality.id}`} variant="info" size="sm">View</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <Alert variant="info" className="mb-0">No municipalities have this issue.</Alert>
            )}
          </Card.Body>
        </Card>
      )}
    </div>
  )
}

export default IssueDetail
